import { useState } from "react";
import axios from "axios";
import Layout from "@/components/Layout";
import { useLang } from "@/context/LanguageContext";
import { ArrowRight, Mail, Instagram, MapPin } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t.pages.contact.success);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(t.pages.contact.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <section className="container-sv pt-24 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Left info */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-px bg-neon-blue" style={{ background: "var(--sv-blue)" }} />
              <span className="font-display uppercase tracking-[0.35em] text-[10px] text-white/60">
                Contact
              </span>
            </div>
            <h1 className="font-display font-black uppercase text-5xl md:text-6xl tracking-tight leading-[0.92]" data-testid="contact-page-title">
              <span className="text-white">Get in </span>
              <span className="neon-text-purple">touch</span>
            </h1>
            <p className="mt-5 text-white/65 font-body text-lg max-w-md">
              {t.pages.contact.subtitle}
            </p>

            <div className="mt-10 space-y-5">
              <a
                href="mailto:hello@seoulvibes.cz"
                className="flex items-center gap-4 text-white/80 hover:text-white group"
                data-testid="contact-email-link"
              >
                <span className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center group-hover:border-purple-400/60">
                  <Mail className="w-4 h-4" />
                </span>
                <span className="font-body">hello@seoulvibes.cz</span>
              </a>
              <a
                href="https://instagram.com/seoulvibes"
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-4 text-white/80 hover:text-white group"
                data-testid="contact-ig-link"
              >
                <span className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center group-hover:border-pink-400/60">
                  <Instagram className="w-4 h-4" />
                </span>
                <span className="font-body">@seoulvibes</span>
              </a>
              <div className="flex items-center gap-4 text-white/60">
                <span className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center">
                  <MapPin className="w-4 h-4" />
                </span>
                <span className="font-body">Prague · Brno · Bratislava</span>
              </div>
            </div>
          </div>

          {/* Right form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={handleSubmit}
              className="bg-[var(--sv-surface)] border border-white/5 rounded-3xl p-6 md:p-10"
              data-testid="contact-form"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field
                  label={t.pages.contact.name}
                  testid="contact-name"
                  value={form.name}
                  onChange={(v) => setForm({ ...form, name: v })}
                  required
                />
                <Field
                  label={t.pages.contact.email}
                  type="email"
                  testid="contact-email"
                  value={form.email}
                  onChange={(v) => setForm({ ...form, email: v })}
                  required
                />
              </div>
              <div className="mt-5">
                <Field
                  label={t.pages.contact.subject}
                  testid="contact-subject"
                  value={form.subject}
                  onChange={(v) => setForm({ ...form, subject: v })}
                />
              </div>
              <div className="mt-5">
                <label className="block font-display uppercase tracking-[0.25em] text-[10px] text-white/55 mb-2">
                  {t.pages.contact.message} *
                </label>
                <textarea
                  rows={6}
                  required
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:border-purple-400/60 focus:outline-none focus:ring-2 focus:ring-purple-500/20 font-body resize-none"
                  data-testid="contact-message"
                  placeholder={lang === "en" ? "Tell us what you have in mind..." : "Napiš nám, co máš na srdci..."}
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="mt-6 sv-btn sv-btn-primary disabled:opacity-50"
                data-testid="contact-submit"
              >
                {loading ? (lang === "en" ? "Sending..." : "Odesílá se...") : t.pages.contact.send}
                <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>
      </section>
    </Layout>
  );
}

function Field({ label, type = "text", value, onChange, testid, required }) {
  return (
    <div>
      <label className="block font-display uppercase tracking-[0.25em] text-[10px] text-white/55 mb-2">
        {label} {required && "*"}
      </label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:border-purple-400/60 focus:outline-none focus:ring-2 focus:ring-purple-500/20 font-body"
      />
    </div>
  );
}
